import React, { useEffect, useState } from "react";
import { Loader2, Download, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";

const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(0)}%`);

// The facts every answer about CASS is judged against. Proposed from the site,
// approved by the administrator — nothing is assumed into place.
export function VerifiedFactsApproval({ onApproved }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [reassessed, setReassessed] = useState(null);

  // Returns nothing on purpose: an effect that returns a promise is treated as a
  // cleanup function and crashes the page on unmount.
  const load = () => {
    api.get("/admin/ai-monitor/profile/proposed").then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(() => {
    load();
  }, []);

  const approve = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/ai-monitor/profile/approve", { fields: edits });
      toast.success(res.data.detail);
      setEdits({});
      load();
      onApproved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve those facts.");
    } finally {
      setBusy(false);
    }
  };

  const reassess = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/ai-monitor/reassess", {});
      // Kept on the page, not only in a toast: which verdicts changed is the
      // actual result, and a toast disappears before it can be read.
      setReassessed(res.data);
      toast.success(res.data.detail);
      onApproved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-assess.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-facts-approval">
      <h4 className="font-bold text-navy text-sm">Verified business facts — for your approval</h4>
      <p className="text-xs text-slate-500 mt-1">{data.note}</p>

      <div className="mt-4 space-y-3">
        {data.fields.map((f) => (
          <div key={f.field} data-testid={`ai-fact-${f.field}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-navy">{f.field.replace(/_/g, " ")}</span>
              {f.needs_confirmation ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                  <AlertCircle size={10} /> needs your confirmation
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                  <CheckCircle2 size={10} /> established on the site
                </span>
              )}
              {f.current && !f.differs && <span className="text-[10px] text-slate-400">already saved</span>}
            </div>
            <Textarea rows={f.proposed && f.proposed.length > 120 ? 3 : 1}
                      defaultValue={f.proposed || f.current || ""}
                      placeholder={f.needs_confirmation ? "Not published on the site — please supply" : ""}
                      onChange={(e) => setEdits((d) => ({ ...d, [f.field]: e.target.value }))}
                      className="mt-1 text-sm" data-testid={`ai-fact-input-${f.field}`} />
            <p className="text-[11px] text-slate-400 mt-0.5">Source: {f.source}</p>
          </div>
        ))}
      </div>

      {(data.biography_facts || []).length > 0 && (
        <div className="mt-5 border-t border-slate-100 pt-4" data-testid="ai-biography-facts">
          <h5 className="text-xs font-bold text-navy">Founder biography — how each detail is classified</h5>
          <p className="text-[11px] text-slate-500 mt-1">{data.biography_note}</p>
          <ul className="mt-3 space-y-2">
            {(data.biography_facts || []).map((b) => (
              <li key={b.fact} className="text-xs" data-testid={`ai-bio-${b.classification}`}>
                <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold mr-2 ${
                  b.classification === "verified_approved" ? "bg-emerald-100 text-emerald-700"
                    : b.classification === "verified_not_required" ? "bg-sky-100 text-sky-700"
                    : b.classification === "unsupported" ? "bg-amber-100 text-amber-800"
                    : "bg-red-100 text-red-700"}`}>
                  {b.classification.replace(/_/g, " ")}
                </span>
                <strong className="text-navy">{b.fact}</strong>
                <span className="block text-slate-600 mt-0.5">{b.note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" onClick={approve} disabled={busy} data-testid="ai-facts-approve">
          {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle2 className="mr-2" size={14} />}
          Approve these facts
        </Button>
        <Button size="sm" variant="outline" onClick={reassess} disabled={busy} data-testid="ai-facts-reassess">
          Re-assess the accuracy findings
        </Button>
      </div>
      {reassessed && (
        <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs" data-testid="ai-reassess-result">
          <p className="font-semibold text-navy">{reassessed.detail}</p>
          {(reassessed.changed || []).length > 0 ? (
            <ul className="mt-2 space-y-1 text-slate-600">
              {reassessed.changed.map((c, i) => (
                <li key={i}>
                  <strong>{c.platform}</strong> — “{c.question}”: {c.from || "no verdict"} → {c.to}
                  {c.notes ? ` (${c.notes})` : ""}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-slate-600">No verdict changed against the approved facts.</p>
          )}
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-2">
        Re-assessing re-reads the answers already held against the approved facts. No platform is queried again,
        so it costs a fraction of a penny and cannot change what was originally said.
      </p>
    </div>
  );
}

export default function MonthlyReport() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/ai-monitor/report").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const download = (path, filename) => {
    const token = localStorage.getItem("cass_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("The download failed."));
  };

  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  const u = data.unbranded;

  return (
    <div className="space-y-4" data-testid="ai-monthly-report">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => download(`/api/admin/ai-monitor/report/pdf?month=${data.month}`,
                                                  `cass-ai-visibility-${data.month}.pdf`)}
                data-testid="ai-report-pdf">
          <FileText size={13} className="mr-1.5" /> Download the report (PDF)
        </Button>
        <Button size="sm" variant="outline" onClick={() => download("/api/admin/ai-monitor/export?fmt=json",
                                                                    "cass-ai-visibility.json")}
                data-testid="ai-report-evidence">
          <Download size={13} className="mr-1.5" /> Full evidence (JSON)
        </Button>
        <Button size="sm" variant="outline" onClick={() => download("/api/admin/ai-monitor/export?fmt=csv",
                                                                    "cass-ai-visibility.csv")}
                data-testid="ai-report-csv">
          <Download size={13} className="mr-1.5" /> Responses (CSV)
        </Button>
        <span className="text-xs text-slate-500 ml-auto">{data.month}</span>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h4 className="font-bold text-navy text-sm">Executive summary</h4>
        <ul className="mt-2 space-y-1.5">
          {data.summary.map((line, i) => (
            <li key={i} className="text-sm text-slate-700 leading-relaxed">{line}</li>
          ))}
        </ul>

        <div className="grid sm:grid-cols-4 gap-3 mt-5">
          {[["Appeared", u.appearance_rate], ["Recommended", u.recommendation_rate],
            ["Cited", u.citation_rate],
            ["Brand accuracy", data.branded_assessable ? data.branded_accurate / data.branded_assessable : null]]
            .map(([label, rate]) => (
              <div key={label} className="rounded-lg bg-slate-50 p-3">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
                <p className="text-xl font-bold text-navy mt-0.5">{pct(rate)}</p>
              </div>
            ))}
        </div>

        {data.open_recommendations.length > 0 && (
          <>
            <h4 className="font-bold text-navy text-sm mt-6">Recommended actions</h4>
            <ul className="mt-2 space-y-2">
              {data.open_recommendations.map((r, i) => (
                <li key={i} className="text-sm text-slate-700">
                  <span className="font-semibold">{r.title}</span>{" "}
                  <span className="text-xs text-slate-500">({r.finding_type}, {r.band} priority)</span>
                  <p className="text-xs text-slate-600 mt-0.5">{r.action}</p>
                </li>
              ))}
            </ul>
          </>
        )}

        <h4 className="font-bold text-navy text-sm mt-6">How to read this</h4>
        <ul className="mt-2 space-y-1">
          {data.caveats.map((c, i) => <li key={i} className="text-[11px] text-slate-500">• {c}</li>)}
        </ul>
        <p className="text-[11px] text-slate-400 mt-2">
          Spend this month £{data.spend_gbp.toFixed(2)} of a £{data.cap_gbp.toFixed(0)} limit.
        </p>
      </section>
    </div>
  );
}
